/**
 * Numbers and times as an operator reads them.
 *
 * A port of ui/.../Format.kt, with one deliberate difference: `clock` used to do its own epoch
 * arithmetic and print UTC while claiming to be local, which is a quiet way to make a log useless.
 * The browser knows the answer, so it is asked.
 *
 * Every moment the server sends is milliseconds since the epoch.
 */

/** What time it is, as the server counts it. */
export function now(): number {
  return Date.now();
}

/**
 * Grouped digits.
 *
 * The counts here run to hundreds of thousands, and `283506` has to be counted rather than read.
 * Not `toLocaleString`, which would put spaces or dots in depending on where the reader is; the
 * server's own logs say `283,506` and the screen should agree with them.
 */
export function grouped(value: number): string {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const MINUTE = 60;
const HOUR = 3600;

/** A duration as the largest two units that say anything, which is how long a thing took. */
export function short(milliseconds: number): string {
  const seconds = Math.floor(milliseconds / 1000);
  if (seconds < MINUTE) return `${seconds}s`;
  if (seconds < HOUR) return `${Math.floor(seconds / MINUTE)}m ${seconds % MINUTE}s`;
  return `${Math.floor(seconds / HOUR)}h ${Math.floor((seconds % HOUR) / MINUTE)}m`;
}

/** How long between two moments. Never negative. */
export function between(from: number, to: number): number {
  return Math.max(0, to - from);
}

/** How long until a moment, said as a wait rather than as a clock time. */
export function until(moment: number, at: number): string {
  if (moment <= at) return "due";
  return `in ${short(between(at, moment))}`;
}

/** How long ago a moment was. */
export function since(moment: number, at: number): string {
  return `${short(between(moment, at))} ago`;
}

/** A fraction as a rounded percentage, for a count against a total. */
export function percent(part: number, whole: number): string {
  if (whole === 0) return "0%";

  const share = (part * 100) / whole;
  if (share > 0 && share < 0.1) return "<0.1%";

  // One decimal, kept even when it is zero, so a column of them lines up.
  return `${(Math.round(share * 10) / 10).toFixed(1)}%`;
}

/** How much longer a run has, going by how fast it has gone so far. */
export function remaining(lookedAt: number, total: number, perSecond: number | null): string | null {
  if (perSecond === null || perSecond <= 0) return null;

  const left = total - lookedAt;
  if (left <= 0) return null;

  return `~${short(Math.floor(left / perSecond) * 1000)} left`;
}

/**
 * A moment as a wall clock, in the reader's own time.
 *
 * The question a log answers is "what was happening when I was here", so it has to be the reader's
 * clock. `toLocaleTimeString` knows the offset and the daylight-saving rule; the Kotlin this
 * replaces worked it out from `getTimezoneOffset` and got UTC.
 */
export function clock(moment: number): string {
  return new Date(moment).toLocaleTimeString(undefined, {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
